import {
  AfterViewInit,
  Component,
  EventEmitter,
  Inject,
  Input,
  NgZone,
  OnDestroy,
  Output,
  PLATFORM_ID,
} from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { HlmButtonImports } from '@spartan-ng/helm/button';
import { environment as env } from '@env/environment';

function rand(n = 16): string {
  const g = globalThis as any;
  if (g?.crypto?.getRandomValues) {
    const a = new Uint8Array(n);
    g.crypto.getRandomValues(a);
    return btoa(String.fromCharCode(...a)).replace(/[^a-z0-9]/gi, '').slice(0, n);
  }

  let s = '';
  for (let i = 0; i < n; i++) s += Math.floor(Math.random() * 36).toString(36);
  return s;
}

@Component({
  selector: 'app-apple-login-button',
  standalone: true,
  imports: [TranslateModule, HlmButtonImports],
  templateUrl: './apple-login-button.component.html',
  styleUrl: "./apple-login-button.component.scss",
})
export class AppleLoginButtonComponent implements AfterViewInit, OnDestroy {
  @Input() clientId: string = env.appleServiceId;
  @Input() redirectURI: string = env.appleRedirectUri;
  @Input() scope: string = env.appleScope ?? 'name email';
  @Input() usePopup: boolean = true;

  @Output() credential = new EventEmitter<string>();
  @Output() error = new EventEmitter<string>();

  private readonly isBrowser: boolean;
  private bootTimer?: ReturnType<typeof setTimeout>;
  private initialized = false;
  private signing = false;

  constructor(@Inject(PLATFORM_ID) platformId: Object, private readonly zone: NgZone) {
    this.isBrowser = isPlatformBrowser(platformId);
  }

  ngAfterViewInit(): void {
    if (!this.isBrowser) return;
    this.ensureSdkAndInit();
  }

  ngOnDestroy(): void {
    if (this.bootTimer) clearTimeout(this.bootTimer);
  }

  private ensureSdkAndInit(): void {
    const api = (window as any).AppleID?.auth;
    if (!api) {
      this.bootTimer = setTimeout(() => this.ensureSdkAndInit(), 150);
      return;
    }
    this.tryInit();
  }

  private tryInit(): void {
    const api = (window as any).AppleID?.auth;
    if (!api) { this.bootTimer = setTimeout(() => this.tryInit(), 150); return; }
    if (this.initialized) return;

    const correctRedirect = this.redirectURI;
    const origInit = api.init?.bind(api);
    if (origInit) {
      (api as any).init = (cfg: any) => {
        const fixed = { ...cfg, redirectURI: correctRedirect };
        return origInit(fixed);
      };
    }

    const origSignIn = api.signIn?.bind(api);
    if (origSignIn) {
      (api as any).signIn = (opts: any) => origSignIn(opts);
    }

    api.init?.({
      clientId: this.clientId,
      scope: this.scope || 'name email',
      redirectURI: correctRedirect,
      usePopup: this.usePopup,
    });

    this.initialized = true;
  }

  triggerApple(): void {
    const api = (window as any).AppleID?.auth;
    if (!api) { this.error.emit('apple_api_unavailable'); return; }
    if (this.signing) return;
    this.signing = true;

    const state = rand(24);
    const nonce = rand(24);

    api.signIn({
      scope: this.scope || 'name email',
      state,
      nonce,
    })
    .then((res: any) => {
      this.signing = false;
      const auth = res?.authorization;
      if (!auth?.id_token || !auth?.code) {
        this.zone.run(() => this.error.emit('apple_missing_code_or_id_token'));
        return;
      }

      this.zone.run(() => this.credential.emit(auth.id_token as string));
    })
    .catch(() => {
      this.signing = false;
      this.zone.run(() => this.error.emit('apple_signin_failed'));
    });
  }
}
